/**
 * API Error Tracking Service
 * Tracks API failure rates and patterns
 */

class ApiErrorTracker {
    constructor() {
        this.errors = [];
        this.maxErrors = 100;
        this.windowSize = 5 * 60 * 1000; // 5 minutes
    }
    
    /**
     * Track API error
     */
    trackError(url, method, status, error) {
        const errorEntry = {
            timestamp: Date.now(),
            url,
            method,
            status,
            error: error?.message || 'Unknown error'
        };
        
        this.errors.push(errorEntry);
        
        // Keep only recent errors
        const cutoff = Date.now() - this.windowSize;
        this.errors = this.errors.filter(e => e.timestamp > cutoff);
        
        // Limit total errors
        if (this.errors.length > this.maxErrors) {
            this.errors = this.errors.slice(-this.maxErrors);
        }
    }
    
    /**
     * Get error rate (errors per minute)
     */
    getErrorRate() {
        const oneMinuteAgo = Date.now() - 60000;
        const recentErrors = this.errors.filter(e => e.timestamp > oneMinuteAgo);
        return recentErrors.length;
    }
    
    /**
     * Get error rate for specific endpoint
     */
    getEndpointErrorRate(url) {
        const oneMinuteAgo = Date.now() - 60000;
        const recentErrors = this.errors.filter(
            e => e.timestamp > oneMinuteAgo && e.url === url
        );
        return recentErrors.length;
    }
    
    /**
     * Get most common errors
     */
    getMostCommonErrors(limit = 5) {
        const errorCounts = {};
        
        this.errors.forEach(error => {
            const key = `${error.url}:${error.status}`;
            errorCounts[key] = (errorCounts[key] || 0) + 1;
        });
        
        return Object.entries(errorCounts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, limit)
            .map(([key, count]) => {
                const [url, status] = key.split(':');
                return { url, status, count };
            });
    }
    
    /**
     * Get error statistics
     */
    getStats() {
        const oneMinuteAgo = Date.now() - 60000;
        const fiveMinutesAgo = Date.now() - 5 * 60000;
        
        const lastMinute = this.errors.filter(e => e.timestamp > oneMinuteAgo).length;
        const lastFiveMinutes = this.errors.filter(e => e.timestamp > fiveMinutesAgo).length;
        
        const statusCounts = {};
        this.errors.forEach(error => {
            statusCounts[error.status] = (statusCounts[error.status] || 0) + 1;
        });
        
        return {
            lastMinute,
            lastFiveMinutes,
            total: this.errors.length,
            statusCounts,
            mostCommon: this.getMostCommonErrors(5)
        };
    }
    
    /**
     * Clear errors
     */
    clear() {
        this.errors = [];
    }
}

// Create singleton instance
const apiErrorTracker = new ApiErrorTracker();

export default apiErrorTracker;
